/**
 * AT Offer - 3 Options Section
 * Handles product option selection, image carousel, and add-to-cart functionality
 */

if (!customElements.get('at-offer-section')) {
  customElements.define('at-offer-section', class extends HTMLElement {
    constructor() {
      super();

      this.options = this.querySelectorAll('.at-offer__option');
      this.optionsContainer = this.querySelector('.at-offer__options');
      this.form = this.querySelector('.at-offer__form');
      this.variantInput = this.querySelector('.at-offer__variant-id');
      this.addButton = this.querySelector('.at-offer__add-button');
      this.buttonText = this.querySelector('.at-offer__button-text');
      // buttonPrice removed — CTA no longer shows price

      // Carousel elements
      this.carousel = this.querySelector('[data-carousel]');
      this.carouselMain = this.querySelector('.at-offer__carousel-main');
      this.carouselThumbs = this.querySelector('[data-thumbs]');

      // Get fallback image
      const fallbackScript = this.querySelector('[data-fallback-image]');
      this.fallbackImage = fallbackScript ? JSON.parse(fallbackScript.textContent) : null;

      this.currentSlideIndex = 0;

      // Track cart insurance state (checked in background on load)
      // State includes: hasInsurance, insuranceType, insuranceLineKey, hasSubscriptionProducts
      this.cartInsuranceState = { hasInsurance: false };
      this.checkCartForInsurance();

      // Re-check cart when page is restored from bfcache (back button)
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.checkCartForInsurance();
        }
      });

      this.bindEvents();

    }

    /**
     * Check cart for existing shipping insurance (runs in background on page load)
     * Tracks detailed state: insurance type, line key, and whether cart has subscription products
     */
    async checkCartForInsurance() {
      const insuranceVariantId = window.theme?.shopSettings?.shippingInsuranceVariantId;

      if (!insuranceVariantId) {
        this.cartInsuranceState = { hasInsurance: false };
        return;
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();

        // Find insurance item in cart
        const insuranceItem = cart.items.find(item =>
          item.variant_id == insuranceVariantId
        );

        // Check if cart has any subscription products (excluding insurance)
        const hasSubscriptionProducts = cart.items.some(item =>
          item.variant_id != insuranceVariantId &&
          item.selling_plan_allocation != null
        );

        // Determine insurance type by checking for selling_plan_allocation
        const insuranceType = insuranceItem
          ? (insuranceItem.selling_plan_allocation ? 'subscription' : 'onetime')
          : null;

        this.cartInsuranceState = {
          hasInsurance: !!insuranceItem,
          insuranceType: insuranceType,
          insuranceLineKey: insuranceItem?.key || null,
          hasSubscriptionProducts: hasSubscriptionProducts
        };
      } catch (error) {
        console.warn('[AT Offer Insurance] Could not check cart:', error);
        this.cartInsuranceState = { hasInsurance: false };
      }
    }

    /**
     * Remove the insurance already in the cart, before the new line is added. Goes through the core by current keys: with
     * a discount code in the cart, Shopify re-keys lines, so a key read earlier can be stale (#PA52681, Oct 19).
     * lineKey is only the fallback when the core isn't loaded.
     */
    async removeInsuranceFromCart(lineKey) {
      const core = window.SubscriptionInsuranceCore;
      if (core?.removeReplacedInsurance) {
        await core.removeReplacedInsurance(null);
        return;
      }
      if (!lineKey) return;

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Failed to remove insurance');
        }
      } catch (error) {
        console.warn('[AT Offer Insurance] Error removing insurance:', error);
        // Continue anyway - better to potentially have duplicate than fail completely
      }
    }

    /**
     * Determine what insurance action to take based on cart state
     * This template is ONE-TIME only, so we always add one-time insurance
     * BUT we preserve subscription insurance if cart has subscription products
     */
    determineInsuranceAction() {
      const state = this.cartInsuranceState;
      const enabled = window.theme?.shopSettings?.shippingInsuranceEnabled;

      if (!enabled) return { action: 'none' };

      if (state.hasInsurance && state.insuranceType === 'subscription') {
        // Subscription insurance exists
        if (state.hasSubscriptionProducts) {
          // Keep subscription insurance - subscription products exist in cart
          return { action: 'none' };
        } else {
          // No subscription products - swap to one-time
          return {
            action: 'swap',
            removeKey: state.insuranceLineKey,
            addType: 'onetime'
          };
        }
      } else if (state.hasInsurance && state.insuranceType === 'onetime') {
        // Already have one-time insurance - keep it
        return { action: 'none' };
      } else {
        // No insurance - add one-time
        return { action: 'add', addType: 'onetime' };
      }
    }

    bindEvents() {
      // Option click handler - use event delegation for robustness
      if (this.optionsContainer) {
        this.optionsContainer.addEventListener('click', (e) => {
          const option = e.target.closest('.at-offer__option');
          if (option && !option.classList.contains('at-offer__option--soldout')) {
            this.selectOption(option);
          }
        });
      }

      // Thumbnail click handler
      if (this.carouselThumbs) {
        this.carouselThumbs.addEventListener('click', (e) => {
          const thumb = e.target.closest('.at-offer__carousel-thumb');
          if (thumb) {
            const index = parseInt(thumb.dataset.thumbIndex, 10);
            this.goToSlide(index);
          }
        });
      }

      // Add-to-cart click handler (click instead of form submit to bypass UpCart interception)
      if (this.addButton) {
        this.addButton.addEventListener('click', this.handleSubmit.bind(this));
      }

      // Sticky bar initialization
      this.initStickyBar();
    }

    initStickyBar() {
      // Cache sticky bar elements (scoped to this section's sticky bar)
      // Sticky bar HTML is after </at-offer-section> closing tag, rendered by snippet
      const sectionWrapper = this.closest('[id^="shopify-section-"]');
      const stickyContainer = sectionWrapper?.querySelector('[id^="sticky-atc-"]');
      this.stickyThumb = stickyContainer?.querySelector('[data-sticky-thumb]');
      this.stickyTitle = stickyContainer?.querySelector('[data-sticky-title]');
      this.stickyPrice = stickyContainer?.querySelector('[data-sticky-price]');
      this.stickyAddButton = stickyContainer?.querySelector('[data-sticky-add]');

      // Populate sticky bar with initial selected option
      const selectedOption = this.querySelector('.at-offer__option--selected');
      if (selectedOption) {
        this.updateStickyBar(selectedOption);
      }

      // Sticky button click - trigger main button click (not form submit, to bypass UpCart)
      if (this.stickyAddButton) {
        this.stickyAddButton.addEventListener('click', () => {
          if (this.addButton) {
            this.addButton.click();
          }
        });
      }
    }

    updateStickyBar(option) {
      if (!this.stickyThumb && !this.stickyTitle && !this.stickyPrice) return;

      // Update thumbnail using safe DOM methods
      if (this.stickyThumb) {
        try {
          const imagesData = option.dataset.images;
          if (imagesData) {
            const images = JSON.parse(imagesData);
            if (images.length > 0) {
              const imgSrc = images[0].thumb || images[0].url;
              // Find existing img or create new one
              let img = this.stickyThumb.querySelector('img');
              if (!img) {
                img = document.createElement('img');
                img.className = 'at-offer__sticky-image';
                img.alt = '';
                this.stickyThumb.appendChild(img);
              }
              img.src = imgSrc;
              img.removeAttribute('srcset'); // Clear srcset so src takes effect
            }
          }
        } catch (e) {
          // Keep existing thumbnail on error
        }
      }

      // Update title
      if (this.stickyTitle) {
        const titleEl = option.querySelector('.at-offer__option-title');
        const stickyLabel = option.dataset.stickyLabel;
        if (stickyLabel || titleEl) {
          this.stickyTitle.textContent = stickyLabel || titleEl.textContent.trim();
        }
      }

      // Update price
      if (this.stickyPrice) {
        const priceFormatted = option.dataset.priceFormatted;
        if (priceFormatted) {
          this.stickyPrice.textContent = priceFormatted;
        }
      }
    }

    selectOption(option) {
      // Prevent re-selecting the same option (guards against double event firing)
      if (option.classList.contains('at-offer__option--selected')) {
        return;
      }

      // Update visual state
      this.options.forEach(opt => {
        opt.classList.remove('at-offer__option--selected');
        const radio = opt.querySelector('input[type="radio"]');
        if (radio) radio.checked = false;
      });

      option.classList.add('at-offer__option--selected');
      const radio = option.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      // Update form data
      const variantId = option.dataset.variantId;
      const priceFormatted = option.dataset.priceFormatted;
      const isAvailable = option.dataset.available === 'true';

      if (this.variantInput) {
        this.variantInput.value = variantId;
      }

      // Update button state
      if (this.addButton) {
        if (isAvailable) {
          this.addButton.disabled = false;
          if (this.buttonText) {
            this.buttonText.textContent = this.addButton.dataset.addText || 'LOCK IN MY DEAL';
          }
        } else {
          this.addButton.disabled = true;
          if (this.buttonText) {
            this.buttonText.textContent = 'SOLD OUT';
          }
        }
      }

      // Update carousel images
      this.updateCarousel(option);

      // Update sticky bar
      this.updateStickyBar(option);
    }

    updateCarousel(option) {
      if (!this.carouselMain) return;

      // Get product images from data attribute
      let images = [];
      try {
        const imagesData = option.dataset.images;
        if (imagesData) {
          images = JSON.parse(imagesData);
        }
      } catch (e) {
        console.error('Error parsing product images:', e);
      }

      // If no images, use fallback
      if (images.length === 0 && this.fallbackImage) {
        images = [{
          url: this.fallbackImage.url,
          thumb: this.fallbackImage.url,
          alt: this.fallbackImage.alt || 'Product image'
        }];
      }

      // If still no images, show placeholder
      if (images.length === 0) {
        this.carouselMain.innerHTML = `
          <div class="at-offer__carousel-slide at-offer__carousel-slide--active" data-slide-index="0">
            <div class="at-offer__placeholder">
              <svg viewBox="0 0 525 525" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="525" height="525" fill="#F5F5F5"/>
                <path d="M262.5 175C220.5 175 186.5 209 186.5 251C186.5 293 220.5 327 262.5 327C304.5 327 338.5 293 338.5 251C338.5 209 304.5 175 262.5 175Z" stroke="#CCCCCC" stroke-width="2"/>
              </svg>
            </div>
          </div>
        `;
        if (this.carouselThumbs) {
          this.carouselThumbs.innerHTML = '';
        }
        return;
      }

      // Build main slides HTML
      const slidesHTML = images.map((img, index) => `
        <div class="at-offer__carousel-slide${index === 0 ? ' at-offer__carousel-slide--active' : ''}" data-slide-index="${index}">
          <img
            src="${img.url}"
            alt="${img.alt}"
            class="at-offer__image"
            loading="${index === 0 ? 'eager' : 'lazy'}"
          >
        </div>
      `).join('');

      this.carouselMain.innerHTML = slidesHTML;

      // Build thumbnails HTML (only if more than 1 image)
      if (images.length > 1) {
        if (!this.carouselThumbs) {
          // Create thumbs container if it doesn't exist
          this.carouselThumbs = document.createElement('div');
          this.carouselThumbs.className = 'at-offer__carousel-thumbs';
          this.carouselThumbs.setAttribute('data-thumbs', '');
          this.carousel.appendChild(this.carouselThumbs);

          // Rebind click handler
          this.carouselThumbs.addEventListener('click', (e) => {
            const thumb = e.target.closest('.at-offer__carousel-thumb');
            if (thumb) {
              const index = parseInt(thumb.dataset.thumbIndex, 10);
              this.goToSlide(index);
            }
          });
        }

        const thumbsHTML = images.map((img, index) => `
          <button
            type="button"
            class="at-offer__carousel-thumb${index === 0 ? ' at-offer__carousel-thumb--active' : ''}"
            data-thumb-index="${index}"
            aria-label="View image ${index + 1}"
          >
            <img src="${img.thumb}" alt="" class="at-offer__thumb-image" loading="lazy">
          </button>
        `).join('');

        this.carouselThumbs.innerHTML = thumbsHTML;
        this.carouselThumbs.style.display = '';
      } else if (this.carouselThumbs) {
        // Hide thumbs if only 1 image
        this.carouselThumbs.style.display = 'none';
      }

      // Reset to first slide
      this.currentSlideIndex = 0;
    }

    goToSlide(index) {
      const slides = this.carouselMain.querySelectorAll('.at-offer__carousel-slide');
      const thumbs = this.carouselThumbs?.querySelectorAll('.at-offer__carousel-thumb');

      if (index < 0 || index >= slides.length) return;

      // Update slides
      slides.forEach((slide, i) => {
        slide.classList.toggle('at-offer__carousel-slide--active', i === index);
      });

      // Update thumbs
      thumbs?.forEach((thumb, i) => {
        thumb.classList.toggle('at-offer__carousel-thumb--active', i === index);
      });

      this.currentSlideIndex = index;
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!this.variantInput || !this.variantInput.value) {
        console.error('No variant selected');
        return;
      }

      const variantId = this.variantInput.value;

      // Add loading state
      if (this.addButton) {
        this.addButton.classList.add('loading');
        this.addButton.disabled = true;
      }
      if (this.stickyAddButton) {
        this.stickyAddButton.classList.add('loading');
        this.stickyAddButton.disabled = true;
      }

      // Build items array - start with the product
      const items = [{
        id: parseInt(variantId, 10),
        quantity: 1
      }];

      // Check for add-on product checkbox
      const addonCheckbox = this.querySelector('.at-offer__addon-checkbox');
      if (addonCheckbox && addonCheckbox.checked && !addonCheckbox.disabled) {
        const addonVariantId = addonCheckbox.dataset.addonVariantId;
        if (addonVariantId) {
          items.push({
            id: parseInt(addonVariantId, 10),
            quantity: 1
          });
        }
      }

      // Smart insurance handling - determines add/swap/none based on cart state
      const insuranceAction = this.determineInsuranceAction();

      // Handle swap - remove existing insurance first
      if (insuranceAction.action === 'swap') {
        await this.removeInsuranceFromCart(insuranceAction.removeKey);
      }

      // Add insurance if needed (for 'add' or 'swap' actions)
      if (insuranceAction.action === 'add' || insuranceAction.action === 'swap') {
        items.push({
          id: parseInt(window.theme.shopSettings.shippingInsuranceVariantId, 10),
          quantity: 1
        });
      }

      try {
        const response = await (window.__nativeFetch || fetch)(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();

        if (response.ok) {
          // Success - refresh cart and open drawer/notification
          this.onAddToCartSuccess(result);
        } else {
          // Error
          this.onAddToCartError(result);
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        this.onAddToCartError({ message: 'Network error. Please try again.' });
      } finally {
        // Remove loading state
        if (this.addButton) {
          this.addButton.classList.remove('loading');
          this.addButton.disabled = false;
        }
        if (this.stickyAddButton) {
          this.stickyAddButton.classList.remove('loading');
          this.stickyAddButton.disabled = false;
        }
      }
    }

    onAddToCartSuccess(result) {
      // Get redirect URL from data attribute, default to /checkout
      const redirectUrl = this.dataset.redirectUrl || '/checkout';

      // Redirect immediately - no delay needed since insurance was added in same request
      window.location.href = redirectUrl;
    }

    async refreshCartDrawer(miniCart) {
      try {
        // Get sections to render from mini-cart if available
        let sections = [];
        if (typeof miniCart.getSectionsToRender === 'function') {
          sections = miniCart.getSectionsToRender().map(section => section.id);
        } else {
          sections = ['mini-cart', 'cart-icon-bubble'];
        }

        const response = await fetch(`${window.Shopify.routes.root}?sections=${sections.join(',')}`);
        const sectionData = await response.json();

        if (typeof miniCart.renderContents === 'function') {
          miniCart.renderContents({ sections: sectionData });
        }

        if (typeof miniCart.open === 'function') {
          miniCart.open();
        }
      } catch (error) {
        console.error('Error refreshing cart drawer:', error);
        // Fallback: just update cart count
        this.updateCartCount();
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();

        // Update cart count badges
        const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count-bubble span, .cart-count');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.closest('.cart-count-bubble')?.classList.remove('hidden');
          }
        });

        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart },
          bubbles: true
        }));
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }

    onAddToCartError(error) {
      console.error('Add to cart failed:', error);

      // Show error message
      const errorMessage = error.message || error.description || 'Could not add to cart. Please try again.';

      // Try to find error container in form
      let errorContainer = this.form?.querySelector('.at-offer__error');

      if (!errorContainer) {
        // Create error container if it doesn't exist
        errorContainer = document.createElement('div');
        errorContainer.className = 'at-offer__error';
        errorContainer.style.cssText = 'color: #dc2626; font-size: 0.875rem; margin-top: 0.5rem; text-align: center;';
        this.form?.appendChild(errorContainer);
      }

      errorContainer.textContent = errorMessage;
      errorContainer.hidden = false;

      // Auto-hide after 5 seconds
      setTimeout(() => {
        errorContainer.hidden = true;
      }, 5000);
    }
  });
}

/**
 * Countdown Timer Component
 * Displays a countdown to a specified date
 */
if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', class extends HTMLElement {
    constructor() {
      super();

      this.targetDate = this.parseDate(this.dataset.date);
      this.daysEl = this.querySelector('[data-days]');
      this.hoursEl = this.querySelector('[data-hours]');
      this.minutesEl = this.querySelector('[data-minutes]');
      this.secondsEl = this.querySelector('[data-seconds]');

      if (this.targetDate) {
        this.startCountdown();
      }
    }

    parseDate(dateString) {
      if (!dateString) return null;

      // Try parsing as ISO date or simple date format
      let date = new Date(dateString);

      // If invalid, try adding time
      if (isNaN(date.getTime())) {
        date = new Date(dateString + 'T23:59:59');
      }

      return isNaN(date.getTime()) ? null : date;
    }

    startCountdown() {
      this.updateCountdown();
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    }

    updateCountdown() {
      const now = new Date();
      const diff = this.targetDate - now;

      if (diff <= 0) {
        // Countdown ended
        clearInterval(this.interval);
        this.setValues(0, 0, 0, 0);
        this.dispatchEvent(new CustomEvent('countdown:ended', { bubbles: true }));
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      this.setValues(days, hours, minutes, seconds);
    }

    setValues(days, hours, minutes, seconds) {
      if (this.daysEl) this.daysEl.textContent = this.padZero(days);
      if (this.hoursEl) this.hoursEl.textContent = this.padZero(hours);
      if (this.minutesEl) this.minutesEl.textContent = this.padZero(minutes);
      if (this.secondsEl) this.secondsEl.textContent = this.padZero(seconds);
    }

    padZero(num) {
      return num.toString().padStart(2, '0');
    }

    disconnectedCallback() {
      if (this.interval) {
        clearInterval(this.interval);
      }
    }
  });
}

/**
 * Read More Button - Product Description Expand/Collapse
 */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-expand]').forEach(btn => {
    const content = btn.previousElementSibling;
    if (!content) return;

    const iconSpan = btn.querySelector('.at-offer__read-more-icon');

    btn.addEventListener('click', () => {
      content.classList.toggle('expanded');
      const isExpanded = content.classList.contains('expanded');

      // Update button text safely using textContent
      btn.childNodes[0].textContent = isExpanded ? 'Read Less ' : 'Read More ';
      if (iconSpan) {
        iconSpan.textContent = isExpanded ? '−' : '+';
      }
    });
  });
});
